import { tv } from "tailwind-variants";
import { Size } from "../helpers/helpers.js";

export const comboboxStyle = tv({
  slots: {
    // The gap spaces an optional Label above and a FieldDescription/FieldError
    // below the field; the popover is portaled, so it never takes part.
    root: "group/combobox flex w-full flex-col gap-2",
    // The [&:focus-within] arbitrary variant targets the native pseudo-class:
    // tailwindcss-react-aria-components remaps `focus-within:` on [data-rac]
    // elements to [data-focus-within], which the ComboBox-managed Group loses
    // while the popup is open (react-aria synthesizes a focus-within blur
    // when focus events fire outside the Group), so the ring would vanish
    // exactly while the popup shows.
    // Read-only looks like disabled (muted, flat) but keeps the white
    // background and stays focusable/selectable.
    field:
      "flex w-full min-w-0 cursor-text items-center rounded-md bg-white text-left text-foreground shadow-xs ring-1 ring-border-strong ring-inset group-data-[disabled]/combobox:cursor-not-allowed group-data-[disabled]/combobox:bg-slate-50 group-data-[disabled]/combobox:text-muted-foreground group-data-[disabled]/combobox:shadow-none group-data-[invalid]/combobox:ring-2 group-data-[invalid]/combobox:ring-red-500 group-data-[readonly]/combobox:text-muted-foreground group-data-[readonly]/combobox:shadow-none [&:focus-within]:ring-2 [&:focus-within]:ring-indigo-600 group-data-[invalid]/combobox:[&:focus-within]:ring-red-500 [&:focus-within]:forced-colors:outline [&:focus-within]:forced-colors:outline-2",
    // Wraps the chips and the input so they reflow together while the chevron
    // stays pinned to the far right of the field. Past five rows of chips it
    // scrolls rather than growing without end — and it cuts off half of the
    // sixth row, so that there is something to see when it does — the field is one row in a form,
    // not a list. `overscroll-contain` keeps the page still once it bottoms out.
    chipsWrap:
      "flex min-w-0 flex-auto flex-wrap items-center gap-1 overflow-y-auto overscroll-contain",
    // Overlays the input on an invisible sizer span. Only the sizer is
    // in-flow, so the input's flex base tracks max(placeholder, typed text):
    // wide enough to never clip the placeholder, and stable while typing so
    // the input doesn't jump between rows.
    inputWrap: "relative min-w-16 flex-auto",
    inputSizer: "invisible block overflow-hidden whitespace-pre",
    // ring-0/outline-hidden are unconditional so they override the
    // @tailwindcss/forms base focus ring (which sets --tw-ring-shadow via a
    // native :focus rule the remapped `focus:` variant can't reach); the
    // field shows the focus ring instead. The explicit font size is needed
    // because @tailwindcss/forms sets font-size on the input element itself,
    // which beats inheritance from the field.
    input:
      "min-w-0 flex-auto border-none bg-transparent p-0 text-inherit placeholder-gray-400 shadow-none ring-0 outline-hidden group-data-[readonly]/combobox:caret-transparent disabled:cursor-not-allowed",
    trigger:
      "flex shrink-0 cursor-default items-center justify-center self-center text-gray-400 outline-hidden",
    chevron: "shrink-0",
    // The highlight reads like text selection: a light indigo fill (fixed
    // palette color rather than the OS-dependent `Highlight` system color, so
    // contrast with the label and remove icon stays predictable). Forced-colors
    // mode drops background colors altogether, so it gets an outline instead —
    // otherwise the highlighted chip is indistinguishable there.
    chip: "flex min-w-0 items-center gap-1 rounded-sm bg-gray-200 font-normal text-foreground select-none group-data-[disabled]/combobox:text-muted-foreground group-data-[readonly]/combobox:text-muted-foreground data-highlighted:bg-indigo-100 data-highlighted:forced-colors:outline data-highlighted:forced-colors:outline-2",
    // The padding widens the pointer target around a ~14px icon; the negative
    // margin lets it grow into the chip's own padding rather than making the
    // chip (and with it the whole field) taller.
    chipRemove:
      "-my-0.5 shrink-0 cursor-pointer rounded-xs p-0.5 text-gray-500 outline-hidden group-data-[disabled]/combobox:hidden group-data-[readonly]/combobox:hidden hover:text-gray-700",
    chipRemoveIcon: "shrink-0",
  },
  variants: {
    size: {
      [Size.SMALL]: {
        field: "min-h-7 gap-1.5 px-2 py-0.5 text-xs leading-4",
        // 5 × 20px chip + 5 × 4px gap + half a chip
        chipsWrap: "max-h-[130px]",
        input: "text-xs leading-4",
        trigger: "-mr-0.5",
        chevron: "size-4",
        chip: "px-1.5 py-0.5",
        chipRemoveIcon: "size-3.5",
      },
      [Size.MEDIUM]: {
        field: "min-h-8 gap-2 px-2.5 py-1 text-sm leading-5",
        // 5 × 24px chip + 5 × 4px gap + half a chip
        chipsWrap: "max-h-[152px]",
        input: "text-sm leading-5",
        trigger: "-mr-1",
        chevron: "size-5",
        chip: "px-1.5 py-0.5",
        chipRemoveIcon: "size-4",
      },
    },
    // Chips sit flush against even padding on all sides; the input keeps its
    // usual text inset via the compound variants below.
    hasChips: {
      true: {
        field: "p-1",
        trigger: "mr-0",
      },
    },
  },
  compoundVariants: [
    // The sizer span carries the same text inset as the input so the
    // measured width stays accurate.
    {
      hasChips: true,
      size: Size.SMALL,
      class: { input: "pl-1", inputSizer: "pl-1" },
    },
    {
      hasChips: true,
      size: Size.MEDIUM,
      class: { input: "pl-1.5", inputSizer: "pl-1.5" },
    },
  ],
  defaultVariants: {
    size: Size.MEDIUM,
  },
});
